/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { ComponentChildren } from 'preact';
import type {
  CurrentRowVariant,
  DataCellFocusTarget,
  DataCellPadding,
  DataCellTooltip,
  TableRendererContext,
  TableRowContext
} from '#UNSAFE_TableView';

import { FormContext } from '#hooks/UNSAFE_useFormContext';
import { classNames } from '#utils/UNSAFE_classNames';
import { getClientHints } from '#utils/PRIVATE_clientHints';
import { preventBrowserTooltipProps } from '#PRIVATE_TableCommon/utils/TooltipUtils';
import { getDataCellClassArray, getFormTextAlign } from './utils/TableThemeUtils';

/**
 * Props for the TableCell Component
 */
type TableCellProps<K, D, C> = {
  tableId: string;
  rowData: D;
  columnKey: C;
  columnIndex: number;
  rowKey: K;
  rowIndex: number;
  isFinalRowIndex: boolean;
  isFinalColumnIndex: boolean;
  isFirstLogicalColumnIndex: boolean;
  isFinalLogicalColumnIndex: boolean;
  hasVerticalGridlines: boolean;
  hasHorizontalGridlines: boolean;
  hasFooters: boolean;
  hasVerticalUnderflow: boolean;
  hasMore: boolean;
  isRowSelected: boolean;
  isPreviousRowSelected: boolean;
  isColumnSelected: boolean;
  isNextColumnSelected: boolean;
  isPreviousColumnSelected: boolean;
  isSticky: boolean;
  isShowFocusRing: boolean;
  hasTabIndex: boolean;
  isTabbableMode: boolean;
  isStickyStartEdge: boolean;
  isStickyEndEdge: boolean;
  hasStartEdge: boolean;
  hasEndEdge: boolean;
  isRtl: boolean;
  isHighContrast: boolean;
  isAccRowHeader: boolean;
  isRowSelectionEnabled: boolean;
  isRowSingleSelection: boolean;
  applyActiveStyle: boolean;
  applyHoverStyle: boolean;
  applyPseudoHoverStyle: boolean;
  field?: keyof D;
  focusTarget?: DataCellFocusTarget<K, D>;
  renderer?: (context: TableRendererContext<K, D, C>) => ComponentChildren;
  selectorRenderer?: () => ComponentChildren;
  variant?: CurrentRowVariant;
  padding?: DataCellPadding<K, D>;
  tooltip?: DataCellTooltip<K, D>;
  horizontalAlignment?: 'start' | 'end' | 'left' | 'right' | 'center';
  verticalAlignment?: 'top' | 'center' | 'bottom';
};

function _defaultCellRenderer<K, D, C>({ rowData, field }: TableRendererContext<K, D, C>) {
  return field != null ? String(rowData[field]) : undefined;
}

/**
 * The internal component used to render a single cell in a TableRow.
 */
export function TableCell<K, D, C>({
  tableId,
  rowData,
  field,
  columnKey,
  columnIndex,
  rowKey,
  rowIndex,
  isFinalRowIndex,
  isFinalColumnIndex,
  isFirstLogicalColumnIndex,
  isFinalLogicalColumnIndex,
  hasVerticalGridlines,
  hasHorizontalGridlines,
  hasFooters,
  hasVerticalUnderflow,
  hasMore,
  isRowSelected,
  isPreviousRowSelected,
  isColumnSelected,
  isNextColumnSelected,
  isPreviousColumnSelected,
  isSticky,
  isShowFocusRing,
  hasTabIndex,
  isTabbableMode,
  isAccRowHeader,
  focusTarget = 'cell',
  renderer = _defaultCellRenderer,
  selectorRenderer,
  isRowSelectionEnabled,
  isRowSingleSelection,
  variant,
  applyActiveStyle,
  applyHoverStyle,
  applyPseudoHoverStyle,
  isStickyStartEdge,
  isStickyEndEdge,
  hasStartEdge,
  hasEndEdge,
  isRtl,
  isHighContrast,
  padding,
  tooltip,
  horizontalAlignment = 'start',
  verticalAlignment = 'center'
}: TableCellProps<K, D, C>) {
  const browser = getClientHints().browser;

  const rowContext: TableRowContext<K, D> = { key: rowKey, data: rowData };
  const columnPadding = typeof padding === 'function' ? padding(rowContext) : padding;
  const columnTooltip = typeof tooltip === 'function' ? tooltip(rowContext) : tooltip;
  const supportsDefaultTooltip = columnTooltip !== 'disabled';
  const hasLastHorizontalGridline =
    hasHorizontalGridlines && (hasMore || hasVerticalUnderflow || !hasFooters);

  const cellClasses = classNames(
    getDataCellClassArray({
      isFirstColumnIndex: columnIndex === 0 || isFirstLogicalColumnIndex,
      isFinalColumnIndex: isFinalColumnIndex || isFinalLogicalColumnIndex,
      isFirstRowIndex: rowIndex === 0,
      isFinalRowIndex,
      hasVerticalGridlines,
      hasHorizontalGridlines,
      hasLastHorizontalGridline,
      isRowSelected,
      isPreviousRowSelected,
      isColumnSelected,
      isNextColumnSelected,
      isPreviousColumnSelected,
      isSticky,
      hasStartEdge,
      hasEndEdge,
      isShowFocusRing,
      isRowSelectionEnabled,
      isRowSingleSelection,
      isActive: applyActiveStyle,
      isHover: applyHoverStyle,
      isPseudoHover: applyPseudoHoverStyle,
      isStickyStartEdge,
      isStickyEndEdge,
      isRtl,
      isHighContrast,
      horizontalAlignment,
      verticalAlignment,
      variant,
      padding: columnPadding
    })
  );

  const cellRendererProps = {
    rowData: rowData,
    rowKey: rowKey,
    field: field,
    columnKey: columnKey,
    selector: selectorRenderer,
    isTabbable: isTabbableMode
  };

  const supportsAutoFocus =
    typeof focusTarget === 'function'
      ? focusTarget(rowContext) === 'content'
      : focusTarget === 'content';

  return (
    <td
      aria-colindex={columnIndex + 1}
      class={cellClasses}
      tabIndex={hasTabIndex ? 0 : -1}
      role={isAccRowHeader ? 'rowheader' : 'gridcell'}
      data-oj-cell-type={'data'}
      data-oj-column-key={columnKey}
      data-oj-table-focusable={tableId}
      data-oj-table-data={tableId}
      data-oj-table-tooltip={supportsDefaultTooltip ? 'enabled' : undefined}
      data-oj-table-auto-focus={supportsAutoFocus ? 'enabled' : undefined}
      {...(supportsDefaultTooltip && browser === 'safari' ? preventBrowserTooltipProps : {})}
      {...(isColumnSelected ? { 'aria-selected': true } : {})}>
      <FormContext.Provider value={{ textAlign: getFormTextAlign(horizontalAlignment, isRtl) }}>
        {renderer(cellRendererProps)}
      </FormContext.Provider>
    </td>
  );
}
